import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { sendEmail } from "./emailService";
import { logError } from "./errorService";

const emailQueueCollection = collection(db, "emailQueue");

/**
 * Agrega un correo a la cola
 * @param {Object} emailData - Datos del correo
 * @param {string} emailData.to - Destinatario
 * @param {string} emailData.subject - Asunto
 * @param {string} emailData.html - Contenido HTML
 * @param {Object} metadata - Metadatos adicionales
 * @returns {Promise<string>} ID del correo en cola
 */
export const queueEmail = async (emailData, metadata = {}) => {
  try {
    const docRef = await addDoc(emailQueueCollection, {
      ...emailData,
      status: "pending",
      attempts: 0,
      lastAttempt: null,
      error: null,
      metadata,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    await logError(error, {
      action: "queue_email",
      stage: "queue",
      emailData,
    });
    throw new Error("Error al agregar el correo a la cola");
  }
};

/**
 * Procesa los correos pendientes en la cola
 * @param {number} batchSize - Cantidad de correos a procesar
 * @returns {Promise<void>}
 */
export const processEmailQueue = async (batchSize = 10) => {
  try {
    // Obtener correos pendientes
    const q = query(
      emailQueueCollection,
      where("status", "==", "pending"),
      orderBy("createdAt", "asc"),
      limit(batchSize)
    );

    const snapshot = await getDocs(q);
    const processingPromises = [];

    for (const doc of snapshot.docs) {
      const emailData = doc.data();
      const processingPromise = processEmail(doc.id, emailData);
      processingPromises.push(processingPromise);
    }

    await Promise.all(processingPromises);
  } catch (error) {
    await logError(error, {
      action: "process_email_queue",
      stage: "processing",
    });
    throw new Error("Error al procesar la cola de correos");
  }
};

/**
 * Procesa un correo individual
 * @param {string} emailId - ID del correo en la cola
 * @param {Object} emailData - Datos del correo
 * @returns {Promise<void>}
 */
const processEmail = async (emailId, emailData) => {
  const maxAttempts = 3;
  const emailRef = doc(emailQueueCollection, emailId);

  try {
    // Actualizar intento
    await updateDoc(emailRef, {
      attempts: emailData.attempts + 1,
      lastAttempt: serverTimestamp(),
    });

    // Intentar enviar el correo
    await sendEmail({
      to: emailData.to,
      subject: emailData.subject,
      body: emailData.html,
    });

    // Marcar como enviado
    await updateDoc(emailRef, {
      status: "sent",
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // Registrar el error
    await logError(error, {
      action: "process_email",
      stage: "sending",
      emailId,
      emailData,
    });

    // Actualizar estado del correo
    const newStatus = emailData.attempts + 1 >= maxAttempts ? "failed" : "pending";
    await updateDoc(emailRef, {
      status: newStatus,
      error: error.message,
      updatedAt: serverTimestamp(),
    });

    if (newStatus === "failed") {
      console.error(`Correo ${emailId} falló después de ${maxAttempts} intentos`);
    }
  }
};

/**
 * Reintenta enviar correos fallidos
 * @param {number} batchSize - Cantidad de correos a reintentar
 * @returns {Promise<void>}
 */
export const retryFailedEmails = async (batchSize = 10) => {
  try {
    const q = query(
      emailQueueCollection,
      where("status", "==", "failed"),
      orderBy("updatedAt", "asc"),
      limit(batchSize)
    );

    const snapshot = await getDocs(q);
    const retryPromises = [];

    for (const doc of snapshot.docs) {
      const emailData = doc.data();
      const retryPromise = updateDoc(doc.ref, {
        status: "pending",
        attempts: 0,
        error: null,
        updatedAt: serverTimestamp(),
      });
      retryPromises.push(retryPromise);
    }

    await Promise.all(retryPromises);
  } catch (error) {
    await logError(error, {
      action: "retry_failed_emails",
      stage: "retry",
    });
    throw new Error("Error al reintentar correos fallidos");
  }
}; 